import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { campaignSchema, describeCampaign } from "@/lib/campaign";

/**
 * A tap on a WhatsApp link.
 *
 * A tap leaves the site for WhatsApp, so this is the only moment it can be
 * recorded. Before it existed, a prospect who chose WhatsApp over the form
 * left no row in the database and no lead in Ads Manager: the message simply
 * appeared on Kristian's phone, with nothing to say which ad had brought it.
 *
 * What is kept is deliberately not personal: the channel, the page and the
 * campaign tags. No name, no number, no IP address, no browser. The IP is
 * used in memory only, so one visitor cannot fill the table or the alert
 * channel, and is never written anywhere.
 */
const tapSchema = z.object({
  channel: z.enum(["whatsapp"]),
  page: z.string().trim().max(200),
  // Defaulted so a page loaded before this field existed can still report.
  campaign: campaignSchema.default({}),
});

const WINDOW_MS = 60_000;
const MAX_PER_WINDOW = 5;
// One alert per visitor per half hour: a second tap is the same person
// finding the link again, not a second enquiry.
const ALERT_QUIET_MS = 30 * 60_000;
const taps = new Map<string, number[]>();
const alerted = new Map<string, number>();

function allow(key: string): { record: boolean; alert: boolean } {
  const now = Date.now();
  const hits = (taps.get(key) ?? []).filter((t) => now - t < WINDOW_MS);
  hits.push(now);
  taps.set(key, hits);
  const record = hits.length <= MAX_PER_WINDOW;
  const alert = record && now - (alerted.get(key) ?? 0) > ALERT_QUIET_MS;
  if (alert) alerted.set(key, now);
  // Keep both maps from growing without bound on a long-lived process.
  if (taps.size > 5000) {
    for (const [k, v] of taps) if (v.every((t) => now - t > WINDOW_MS)) taps.delete(k);
    for (const [k, t] of alerted) if (now - t > ALERT_QUIET_MS) alerted.delete(k);
  }
  return { record, alert };
}

export const recordContactTap = createServerFn({ method: "POST" })
  .validator((data: unknown) => tapSchema.parse(data))
  .handler(async ({ data }) => {
    let visitor = "anon";
    try {
      const { getRequestIP } = await import("@tanstack/react-start/server");
      visitor = getRequestIP({ xForwardedFor: true }) ?? "anon";
    } catch {
      // Not fatal: everyone shares one bucket rather than the tap being lost.
    }

    const { record, alert } = allow(visitor);
    if (!record) return { recorded: false };

    // Like a lead, a tap is saved first and announced second, and neither
    // step may fail the visitor's click: WhatsApp is already opening.
    let recorded = true;
    try {
      const { getDb } = await import("@/lib/db.server");
      await getDb().query(
        "INSERT INTO contact_taps (channel, page, utm_source, utm_medium, utm_campaign, utm_content) VALUES ($1, $2, $3, $4, $5, $6)",
        [
          data.channel,
          data.page || null,
          data.campaign.source ?? null,
          data.campaign.medium ?? null,
          data.campaign.campaign ?? null,
          data.campaign.content ?? null,
        ],
      );
    } catch (error) {
      console.error("[contact-tap] insert failed:", error);
      recorded = false;
    }

    if (alert) {
      const { notifyNewLead } = await import("@/lib/notify.server");
      await notifyNewLead("contact-tap", {
        title: "Someone tapped WhatsApp on adkiro.com",
        fields: [
          ["Page", data.page || "/"],
          ["Came from", describeCampaign(data.campaign)],
          ["What to expect", "A WhatsApp message may follow. Nothing else is known about them."],
        ],
      });
    }

    return { recorded };
  });
